import React, { useCallback, useEffect, useState } from 'react';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TeamUiModel } from '../../../shared/ui-models/team.ui-model';
import { AccessControlFilterConfigModel } from '../../../shared/models/team.model';
import {
  createTeam,
  deleteTeam,
  getAccessControlConfig,
  getTeams,
  updateTeam
} from '../../../shared/services/data/team.data';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { LoadingData } from '../../../ui/LoadingData';
import { NotFoundData } from '../../../ui/NotFoundData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import { TeamFormModal, TeamFormValue } from './components/team-form-modal.component';
import { CHIP } from '../../../LeadModule/shared/lead-visuals';

export const TeamsListContainer: React.FC = () => {
  const [teams, setTeams] = useState<TeamUiModel[]>([]);
  const [filterConfig, setFilterConfig] = useState<AccessControlFilterConfigModel>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TeamUiModel | undefined>();

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([getTeams(), getAccessControlConfig()]).then(
      ([teamsResult, configResult]) => {
        setLoadError(isErrorInfo(teamsResult) ? describeLoadError(teamsResult) : undefined);
        if (!isErrorInfo(teamsResult)) setTeams(teamsResult);
        if (!isErrorInfo(configResult)) setFilterConfig(configResult);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = (value: TeamFormValue) => {
    const isEdit = Boolean(editing);
    const request = isEdit && editing ? updateTeam(editing.id, value) : createTeam(value);

    const promise = request.then((response: unknown) => {
      if (isErrorInfo(response as ErrorInfo)) throw response;
      setShowForm(false);
      setEditing(undefined);
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: isEdit ? 'Saving team...' : 'Creating team...',
      successMessage: isEdit ? 'Team saved' : 'Team created',
      errorPermissionMessage: 'You do not have permission to change teams',
      errorFailedMessage: isEdit ? 'Failed to save team' : 'Failed to create team'
    });
  };

  const handleDelete = (team: TeamUiModel) => {
    const promise = deleteTeam(team.id).then((response: boolean | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Archiving team...',
      successMessage: 'Team archived',
      errorPermissionMessage: 'You do not have permission to archive a team',
      errorFailedMessage: 'Failed to archive team'
    });
  };

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section className="flex flex-wrap items-center gap-3">
        <span className="h-8 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
        <h1 className="font-gilroy-bold text-2xl text-neutral-500">Teams &amp; access</h1>
        {!loading ? (
          <span className={`${CHIP} bg-primary-50 text-primary-700`}>
            {teams.length} team{teams.length === 1 ? '' : 's'}
          </span>
        ) : null}
        <span className="ml-auto">
          <Button
            type={ButtonType.primary}
            onClick={() => {
              setEditing(undefined);
              setShowForm(true);
            }}
          >
            Create team
          </Button>
        </span>
      </section>

      <section>
        {loading ? (
          <LoadingData />
        ) : loadError ? (
          <LoadError message={loadError} onRetry={load} />
        ) : teams.length === 0 ? (
          <NotFoundData />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-md">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="bg-creams-200">
                  {['Team', 'Lead access', ''].map((column: string) => (
                    <th
                      key={column || 'actions'}
                      className="whitespace-nowrap px-3 py-3 font-gilroy-bold text-neutral-500"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teams.map((team: TeamUiModel) => {
                  const leadGrant = team.grants.find((grant) => grant.module === 'leads');
                  return (
                    <tr key={team.id} className="hover:bg-creams-100">
                      <td className="border-t border-gray-100 px-3 py-3">
                        <span className="font-gilroy-medium text-neutral-500">{team.name}</span>
                        {team.isArchived ? (
                          <span className={`ml-2 ${CHIP} bg-neutral-50 text-neutral-300`}>
                            Archived
                          </span>
                        ) : null}
                      </td>
                      <td className="border-t border-gray-100 px-3 py-3">
                        {!leadGrant ? (
                          <span className="text-xs text-neutral-300">no lead grant</span>
                        ) : leadGrant.isUnconditional ? (
                          // The most permissive thing a team can hold, and it
                          // renders as an empty cell unless it is called out.
                          <span
                            className={`${CHIP} bg-secondary-50 text-secondary-700`}
                            title="The most permissive grant a team can hold"
                          >
                            All leads
                          </span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {leadGrant.filters.map((filter) => (
                              <span
                                key={filter.key}
                                className={`${CHIP} bg-primary-50 text-primary-700`}
                              >
                                {filter.key}: {filter.values.join(', ')}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap text-right">
                        <button
                          type="button"
                          className="text-sm text-primary-500"
                          onClick={() => {
                            setEditing(team);
                            setShowForm(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ml-3 text-sm text-action-danger"
                          onClick={() => handleDelete(team)}
                        >
                          Archive
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <TeamFormModal
        show={showForm}
        team={editing}
        filterConfig={filterConfig}
        onClose={() => {
          setShowForm(false);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
